'use client'

import React from 'react'
import {
  Check,
  X,
  Zap,
  Server,
  Brain,
  Crown,
  ArrowRightLeft,
} from 'lucide-react'
import { useTranslations } from 'next-intl'

/**
 * Comparison table component showing HeyBoss AI vs DIY Builders vs Web Agency
 */
export function Comparison() {
  const t = useTranslations('home_v3.comparison')

  return (
    <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6 py-16 md:py-24 border-t border-white/5">
      {/* Header */}
      <div className="text-center mb-12 md:mb-16">
        <h2 className="text-3xl md:text-6xl font-bold text-white mb-4 md:mb-6 tracking-tighter leading-tight">
          {t('header.title_line1')} <br />
          <span className="text-transparent bg-clip-text bg-gold-gradient drop-shadow-lg">
            {t('header.title_line2')}
          </span>
        </h2>
        <p className="text-base md:text-lg text-gray-400 max-w-3xl mx-auto font-light leading-relaxed px-4">
          {t('header.subtitle')}
        </p>
      </div>

      {/* Mobile Swipe Instruction */}
      <div className="md:hidden flex items-center justify-center gap-2 mb-4 text-gold animate-pulse">
        <ArrowRightLeft className="w-4 h-4" />
        <span className="text-xs font-bold uppercase tracking-widest">
          Swipe to compare
        </span>
      </div>

      {/* Unified Comparison Table Container with Horizontal Scroll for Mobile */}
      <div className="relative rounded-3xl border border-white/10 bg-[#0A0A0A]/50 backdrop-blur-sm shadow-2xl overflow-hidden">
        {/* Right side fade gradient for mobile to indicate scroll */}
        <div className="absolute top-0 right-0 bottom-0 w-12 bg-gradient-to-l from-[#0A0A0A] via-black/50 to-transparent z-40 pointer-events-none md:hidden"></div>

        {/* Scrollable Wrapper */}
        <div className="overflow-x-auto hide-scrollbar scroll-smooth">
          <div className="min-w-[1100px]">
            {/* Background Glow */}
            <div className="absolute top-0 right-0 w-1/4 h-full bg-gold/5 blur-[80px] pointer-events-none"></div>

            {/* --- TABLE HEADER --- */}
            <div className="grid grid-cols-4 border-b border-white/10 bg-black/40 sticky top-0 z-10">
              <div className="p-6 flex items-end text-gray-500 text-xs font-semibold uppercase tracking-wider">
                {t('table_headers.features')}
              </div>
              <div className="p-6 text-center">
                <h3 className="text-lg font-bold text-white mb-1">
                  {t('table_headers.diy_builders')}
                </h3>
                <p className="text-[10px] text-gray-500 font-medium">
                  {t('table_headers.diy_subtitle')}
                </p>
              </div>
              <div className="p-6 text-center border-l border-white/5">
                <h3 className="text-lg font-bold text-white mb-1">
                  {t('table_headers.web_agency')}
                </h3>
                <p className="text-[10px] text-gray-500 font-medium">
                  {t('table_headers.agency_subtitle')}
                </p>
              </div>
              <div className="relative p-6 text-center bg-white/5 border-l border-white/10 overflow-hidden">
                <div className="absolute inset-0 bg-gold-gradient opacity-10"></div>
                <div className="absolute top-0 left-0 w-full h-1 bg-gold-gradient"></div>
                <h3 className="relative text-xl font-bold text-transparent bg-clip-text bg-gold-gradient mb-1 flex items-center justify-center gap-2">
                  <Crown className="w-5 h-5 fill-gold text-gold" />{' '}
                  {t('table_headers.heyboss_ai')}
                </h3>
                <p className="relative text-[10px] text-gold/80 font-bold tracking-wide uppercase">
                  {t('table_headers.heyboss_subtitle')}
                </p>
              </div>
            </div>

            {/* --- SECTION 1: BUILD --- */}
            <div className="bg-white/[0.02]">
              <div className="px-6 py-3 border-y border-white/10 bg-white/[0.02] flex items-center gap-2">
                <Zap className="w-4 h-4 text-blue-400" />
                <span className="text-sm font-bold text-white tracking-wide">
                  {t('build_phase.title')}
                </span>
              </div>

              {/* Row: Type */}
              <div className="grid grid-cols-4 border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                <div className="p-5 text-sm text-gray-400 font-medium flex items-center">
                  {t('build_phase.dev_type')}
                </div>
                <div className="p-5 text-center text-sm text-gray-300">
                  {t('build_phase.dev_type_diy')}
                </div>
                <div className="p-5 text-center text-sm text-gray-300 border-l border-white/5">
                  {t('build_phase.dev_type_agency')}
                </div>
                <div className="p-5 text-center text-sm font-bold text-white border-l border-white/10 bg-white/5 relative">
                  {t('build_phase.dev_type_heyboss')}
                </div>
              </div>

              {/* Row: Cost */}
              <div className="grid grid-cols-4 border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                <div className="p-5 text-sm text-gray-400 font-medium flex items-center">
                  {t('build_phase.cost')}
                </div>
                <div className="p-5 text-center text-sm text-gray-300">
                  {t('build_phase.cost_diy')}
                </div>
                <div className="p-5 text-center text-sm text-gray-300 border-l border-white/5">
                  {t('build_phase.cost_agency')}
                </div>
                <div className="p-5 text-center text-sm border-l border-white/10 bg-white/5 relative flex flex-col justify-center gap-1">
                  <span className="text-gold font-bold block">
                    {t('build_phase.cost_heyboss_simple')}
                    <span className="text-[10px] text-gray-400 font-normal">
                      {t('build_phase.cost_credits')}
                    </span>
                  </span>
                  <span className="text-gray-400 text-2xs">
                    {t('build_phase.cost_heyboss_simple_description')}
                  </span>
                  <span className="text-gold font-bold block">
                    {t('build_phase.cost_heyboss_complex')}
                    <span className="text-[10px] text-gray-400 font-normal">
                      {t('build_phase.cost_credits')}
                    </span>
                  </span>
                  <span className="text-gray-400 text-2xs">
                    {t('build_phase.cost_heyboss_complex_description')}
                  </span>
                </div>
              </div>

              {/* Row: Time */}
              <div className="grid grid-cols-4 border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                <div className="p-5 text-sm text-gray-400 font-medium flex items-center">
                  {t('build_phase.time')}
                </div>
                <div className="p-5 text-center text-sm text-gray-300">
                  {t('build_phase.time_diy')}
                </div>
                <div className="p-5 text-center text-sm text-gray-300 border-l border-white/5">
                  {t('build_phase.time_agency')}
                </div>
                <div className="p-5 text-center text-sm font-bold text-white border-l border-white/10 bg-white/5 relative">
                  {t('build_phase.time_heyboss')}
                </div>
              </div>
            </div>

            {/* --- SECTION 2: HOSTING --- */}
            <div className="bg-white/[0.02]">
              <div className="px-6 py-3 border-y border-white/10 bg-white/[0.02] flex items-center gap-2">
                <Server className="w-4 h-4 text-purple-400" />
                <span className="text-sm font-bold text-white tracking-wide">
                  {t('hosting.title')}
                </span>
              </div>

              {/* Row: Updates */}
              <div className="grid grid-cols-4 border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                <div className="p-5 text-sm text-gray-400 font-medium flex items-center">
                  {t('hosting.updates')}
                </div>
                <div className="p-5 text-center text-sm text-gray-300">
                  {t('hosting.updates_diy')}
                </div>
                <div className="p-5 text-center text-sm text-gray-300 border-l border-white/5">
                  {t('hosting.updates_agency')}
                </div>
                <div className="p-5 text-center text-sm font-bold text-white border-l border-white/10 bg-white/5 relative">
                  {t('hosting.updates_heyboss')}
                </div>
              </div>

              {/* Row: Monthly Cost */}
              <div className="grid grid-cols-4 border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                <div className="p-5 text-sm text-gray-400 font-medium flex items-center">
                  {t('hosting.monthly_cost')}
                </div>
                <div className="p-5 text-center text-sm text-gray-300">
                  {t('hosting.monthly_cost_diy')}
                </div>
                <div className="p-5 text-center text-sm text-gray-300 border-l border-white/5">
                  {t('hosting.monthly_cost_agency')}
                </div>
                <div className="p-5 text-center text-sm border-l border-white/10 bg-white/5 relative">
                  <span className="font-bold text-gold text-lg">
                    {t('hosting.monthly_cost_heyboss')}{' '}
                    <span className="text-xs text-gray-400 font-normal">
                      {t('hosting.monthly_cost_per_month')}
                    </span>
                  </span>
                  <div className="text-[10px] text-gray-500 mt-1">
                    {t('hosting.monthly_cost_note')}
                  </div>
                </div>
              </div>
            </div>

            {/* --- SECTION 3: MARKETING --- */}
            <div className="bg-white/[0.02]">
              <div className="px-6 py-3 border-y border-white/10 bg-white/[0.02] flex items-center gap-2">
                <Brain className="w-4 h-4 text-green-400" />
                <span className="text-sm font-bold text-white tracking-wide">
                  {t('marketing.title')}
                </span>
              </div>

              {/* Row: SEO */}
              <div className="grid grid-cols-4 border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                <div className="p-5 text-sm text-gray-400 font-medium flex items-center">
                  {t('marketing.seo')}
                </div>
                <div className="p-5 text-center border-l border-white/5 flex justify-center">
                  <span className="text-xs font-bold text-red-500/70 flex items-center gap-1">
                    <X className="w-3 h-3" /> {t('marketing.not_included')}
                  </span>
                </div>
                <div className="p-5 text-center text-sm text-gray-300 border-l border-white/5">
                  {t('marketing.seo_agency')}
                </div>
                <div className="p-5 text-center border-l border-white/10 bg-white/5 relative flex justify-center">
                  <span className="text-xs font-bold text-gold flex items-center gap-1 px-2 py-1 rounded bg-gold/10 border border-gold/20">
                    <Check className="w-3 h-3" />{' '}
                    {t('marketing.included_in_plan')}
                  </span>
                </div>
              </div>

              {/* Row: CRM */}
              <div className="grid grid-cols-4 hover:bg-white/[0.02] transition-colors">
                <div className="p-5 text-sm text-gray-400 font-medium flex items-center">
                  {t('marketing.crm')}
                </div>
                <div className="p-5 text-center border-l border-white/5 flex justify-center">
                  <span className="text-xs font-bold text-red-500/70 flex items-center gap-1">
                    <X className="w-3 h-3" /> {t('marketing.not_included')}
                  </span>
                </div>
                <div className="p-5 text-center text-sm text-gray-300 border-l border-white/5">
                  {t('marketing.crm_agency')}
                </div>
                <div className="p-5 text-center border-l border-white/10 bg-white/5 relative flex justify-center">
                  <span className="text-xs font-bold text-gold flex items-center gap-1 px-2 py-1 rounded bg-gold/10 border border-gold/20">
                    <Check className="w-3 h-3" />{' '}
                    {t('marketing.included_in_plan')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="text-center mt-8 space-y-2 px-4">
        <p className="text-xs text-gray-500 italic max-w-2xl mx-auto">
          {t('footer.note')}
        </p>
        <a
          href="https://heyboss.ai/pricing"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block mt-4 px-6 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-gray-300 hover:text-gold transition-all duration-300 cursor-pointer relative z-50"
        >
          {t('footer.pricing_link')}
        </a>
      </div>
    </div>
  )
}
